import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SelectProps } from '@material-ui/core/Select'

const RESET_DELAY = 1500

const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

// Counts characters of `label`, not of `query`, so the result stays a valid slice index
// even when folding changes length. Returns -1 when `label` does not match.
export const typeaheadPrefixLength = (label: string, query: string) => {
  if (!query) {
    return 0
  }

  const folded = fold(query)

  for (let i = 1; i <= label.length; i++) {
    const candidate = fold(label.slice(0, i))

    if (candidate === folded) {
      return i
    }

    if (!folded.startsWith(candidate)) {
      return -1
    }
  }

  return -1
}

type Typeahead = {
  query: string
  selectProps: Pick<SelectProps, 'MenuProps' | 'onClose'>
}

/** `labels` must be in the same order as the rendered `MenuItem`s. */
export const useSelectTypeahead = (
  enabled: boolean,
  labels: string[],
): Typeahead => {
  const [query, setQuery] = useState('')
  const state = useRef({ buffer: '', repeating: false, timer: 0 })
  const labelsRef = useRef(labels)

  labelsRef.current = labels

  const reset = useCallback(() => {
    window.clearTimeout(state.current.timer)
    state.current = { buffer: '', repeating: false, timer: 0 }
    setQuery('')
  }, [])

  // `repeating` is stored rather than derived: a one-character buffer always looks like a
  // repeat, so backspacing "to" down to "t" would jump focus off the item being read.
  const commit = useCallback(
    (buffer: string, repeating: boolean) => {
      window.clearTimeout(state.current.timer)
      state.current = {
        buffer,
        repeating,
        timer: window.setTimeout(reset, RESET_DELAY),
      }
      setQuery(buffer)
    },
    [reset],
  )

  useEffect(() => () => window.clearTimeout(state.current.timer), [])

  const onKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      const native = event.nativeEvent

      if (native.isComposing || native.keyCode === 229) {
        return
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      const { buffer, repeating } = state.current
      const isCharacter =
        event.key.length === 1 && (event.key !== ' ' || Boolean(buffer))
      const isBackspace = event.key === 'Backspace' && Boolean(buffer)

      if (!isCharacter && !isBackspace) {
        return
      }

      // Capturing pre-empts MenuList's own bubble-phase typeahead, but it also skips
      // ButtonBase's preventDefault for Space, which would otherwise scroll the menu.
      event.preventDefault()
      event.stopPropagation()

      if (isBackspace) {
        commit(buffer.slice(0, -1), false)

        return
      }

      const options = Array.from(
        event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]'),
      )

      if (!options.length) {
        return
      }

      const from = options.indexOf(document.activeElement as HTMLElement)

      const focusMatch = (prefix: string) => {
        for (let i = 0; i < options.length; i++) {
          const index = (from + 1 + i) % options.length

          if (
            typeaheadPrefixLength(labelsRef.current[index] || '', prefix) > 0
          ) {
            options[index].focus()

            return true
          }
        }

        return false
      }

      // Must precede the miss check below, or a second "t" would try to match "tt".
      if (repeating && fold(buffer) === fold(event.key)) {
        focusMatch(buffer)
        commit(buffer, true)

        return
      }

      const next = buffer + event.key

      if (
        from > -1 &&
        typeaheadPrefixLength(labelsRef.current[from] || '', next) > 0
      ) {
        commit(next, next.length === 1)

        return
      }

      if (focusMatch(next)) {
        commit(next, next.length === 1)
      }
    },
    [commit],
  )

  // Gated in one place so that when disabled nothing at all reaches `Select` and MUI's
  // own typeahead stays in charge.
  return useMemo(
    () =>
      enabled
        ? {
            query,
            selectProps: {
              // Menu sets onKeyDown, actions, autoFocus, autoFocusItem and variant before
              // spreading MenuListProps, so nothing else may be passed through here.
              MenuProps: { MenuListProps: { onKeyDownCapture } },
              onClose: reset,
            },
          }
        : { query: '', selectProps: {} },
    [enabled, query, onKeyDownCapture, reset],
  )
}
